import { useCallback, useState } from 'react';

import { categoryApi } from '@/api/services/categoryApi';
import { viewApi } from '@/api/services/viewApi';
import type { ChinaAppSysView } from '@/types/entities';
import { entityTypeDisplayNames } from '@/types/enums';

import { useAdminChanges } from '../adminChanges';
import { PickerDialog, type PickerOption } from '../PickerDialog';
import { useAdminList } from './adminList';
import { AdminSubList } from './AdminSubList';

/**
 * Views einer Kategorie (REQ-28 §4) — im Altclient gab es die Liste nicht. Einen Endpunkt
 * „Views einer Kategorie" hat die API nicht (`GET categories/{id}` lädt die Kategorie ohne
 * `Include(x => x.Views)`); gelesen wird deshalb über `CategoryId` der Views.
 */
export interface AdminCategoryViewsListProps {
  parentId: number;
}

const COLUMNS = ['Bezeichnung', 'Entity'] as const;

/** Der Satz steht so in REQ-28 §4. */
const ONE_CATEGORY_HINT =
  'Eine View gehört zu höchstens einer Kategorie. Umhängen geht über das Formular der View.';

export function AdminCategoryViewsList(props: AdminCategoryViewsListProps) {
  const { parentId } = props;
  const changes = useAdminChanges();
  const load = useCallback(
    async () => (await viewApi.get()).filter((view) => view.CategoryId === parentId),
    [parentId],
  );
  const { rows, loading, error, reload } = useAdminList(load);
  const [options, setOptions] = useState<PickerOption[] | null>(null);
  const [busy, setBusy] = useState(false);

  // `POST categories/{id}/views/add/{viewId}` antwortet mit 400, wenn die View schon zu einer
  // Kategorie gehört — angeboten werden deshalb nur Views ohne Kategorie.
  const openPicker = async () => {
    setBusy(true);
    const all = await viewApi.get();
    setOptions(
      all
        .filter((view) => view.CategoryId === null || view.CategoryId === undefined)
        .map((view) => ({
          id: view.Id,
          label: view.ViewName ?? '',
          description: entityTypeDisplayNames[view.Entity],
        })),
    );
    setBusy(false);
  };

  const change = async (run: () => Promise<unknown>) => {
    setBusy(true);
    await run();
    await reload();
    setBusy(false);
    changes.notifyNavigationChanged();
  };

  return (
    <>
      <AdminSubList<ChinaAppSysView>
        title="Views"
        columns={COLUMNS}
        rows={rows}
        rowKey={(view) => view.Id}
        renderCells={(view) => (
          <>
            <td>{view.ViewName}</td>
            <td>{entityTypeDisplayNames[view.Entity]}</td>
          </>
        )}
        loading={loading}
        error={error}
        onRetry={() => {
          void reload();
        }}
        busy={busy}
        onAdd={() => {
          void openPicker();
        }}
        onRemove={(view) => {
          void change(() => categoryApi.removeView(parentId, view.Id));
        }}
        emptyText="Keine Views zugeordnet"
        errorMessage="Die Views konnten nicht geladen werden."
        hint={ONE_CATEGORY_HINT}
      />
      <PickerDialog
        open={options !== null}
        title="Views auswählen"
        options={options ?? []}
        emptyText="Es gibt keine View ohne Kategorie."
        onClose={() => {
          setOptions(null);
        }}
        onSubmit={async (ids) => {
          await change(async () => {
            for (const id of ids) await categoryApi.addView(parentId, id);
          });
        }}
      />
    </>
  );
}

export default AdminCategoryViewsList;
